'use client'

import { type Options, documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { useMemo } from 'react'
import styled from 'styled-components'

import { TabGroupList, TabGroupTab, isUppercase } from '@syconium/little-miss-figgy'
import { TypeStyle, getTypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd } from '@syconium/little-miss-figgy/dist/themes/default/breakpoints'

import { gql } from '../../../../../__generated__/graphql/catalog'
import {
  PageSectionHeaderFragmentFragment,
  PageTabGroupSectionFragmentFragment,
} from '../../../../../__generated__/graphql/catalog/graphql'

import { ContentfulStyledLink } from './ContentfulStyledLink'
import {
  createDefaultRichTextRenderMark,
  createRichTextHyperlinkRenderer,
  getContentfulRichTextPlainText,
  getNonEmptySanitizedContentfulDocument,
} from './ContentfulStyledTextRichTextUtils'

export const pageSectionHeaderQueryFragment = gql(`
  fragment PageSectionHeaderFragment on PageSectionHeader {
    __typename
    id
    titleRichText
    titleTextSize
    paragraphRichText
    paragraphTextSize
    textContentMaxWidthMobile
    textContentMaxWidthDesktop
    textAlignMobile
    textAlignDesktop
    links {
      ...StyledLinkFragment
    }
    hasVerticalMargin
  }
`)

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4'

export type SectionHeaderTabItem = { id: string; text: string }

type ContentfulPageSectionHeaderProps = {
  titleRichText?: PageSectionHeaderFragmentFragment['titleRichText']
  titleTextSize?: PageSectionHeaderFragmentFragment['titleTextSize']
  paragraphRichText?: PageSectionHeaderFragmentFragment['paragraphRichText']
  paragraphTextSize?: PageSectionHeaderFragmentFragment['paragraphTextSize']
  textContentMaxWidthMobile?: PageSectionHeaderFragmentFragment['textContentMaxWidthMobile']
  textContentMaxWidthDesktop?: PageSectionHeaderFragmentFragment['textContentMaxWidthDesktop']
  textAlignMobile: TextAlign
  textAlignDesktop: TextAlign
  links?: PageSectionHeaderFragmentFragment['links']
  tabItems?: SectionHeaderTabItem[]
  titleAsTag?: HeadingTag
  spacingBelow?: SpacingBelow
}

type TextAlign =
  | PageSectionHeaderFragmentFragment['textAlignMobile']
  | PageTabGroupSectionFragmentFragment['textAlignMobile']
type SpacingBelow = 'default' | 'compact'
type TextContentMaxWidth = ContentfulPageSectionHeaderProps['textContentMaxWidthMobile']
type TitleTextSize = ContentfulPageSectionHeaderProps['titleTextSize']
type ParagraphTextSize = ContentfulPageSectionHeaderProps['paragraphTextSize']

const titleTextSizeToHeadingTag: Record<NonNullable<TitleTextSize>, HeadingTag> = {
  headlineExtraLarge: 'h1',
  headlineLarge: 'h2',
  headlineMedium: 'h3',
  headlineSmall: 'h4',
}

const HeaderOverlay = styled.div<{
  $textAlignMobile: TextAlign
  $textAlignDesktop: TextAlign
  $spacingBelow: SpacingBelow
}>`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(5)};
  margin-bottom: ${o => o.theme.spacing(o.$spacingBelow === 'compact' ? 5 : 10)};

  text-align: ${o => o.$textAlignMobile};

  ${fromMd} {
    text-align: ${o => o.$textAlignDesktop};

    ${o =>
      o.$textAlignDesktop === 'left' &&
      ` flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
      `}
  }
`

const toMaxWidth = (maxWidth: TextContentMaxWidth) => (maxWidth ? `${maxWidth}px` : 'none')

// Auto inline margins center the capped text block; left-aligned text stays flush start.
const HeaderText = styled.div<{
  $textAlignMobile: TextAlign
  $textAlignDesktop: TextAlign
  $textContentMaxWidthMobile: TextContentMaxWidth
  $textContentMaxWidthDesktop: TextContentMaxWidth
}>`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(5)};

  max-width: ${o => toMaxWidth(o.$textContentMaxWidthMobile)};
  margin-inline: ${o => (o.$textAlignMobile === 'center' ? 'auto' : 0)};

  ${fromMd} {
    max-width: ${o => toMaxWidth(o.$textContentMaxWidthDesktop)};
    margin-inline: ${o => (o.$textAlignDesktop === 'center' ? 'auto' : 0)};
  }
`

type HeaderTitleProps = {
  className?: string
  children?: React.ReactNode
  isUppercase: boolean
  titleTextSize: TitleTextSize
  asTag?: HeadingTag
}

const HeaderTitle = styled(
  ({ className, children, isUppercase, titleTextSize, asTag }: HeaderTitleProps) => {
    const size = titleTextSize ?? 'headlineMedium'
    const TypeStyleSelection = getTypeStyle(size)
    const headingTag = asTag ?? titleTextSizeToHeadingTag[size]
    return (
      <TypeStyleSelection
        color='primary'
        asTag={headingTag}
        className={className}
        isUppercase={isUppercase}
      >
        {children}
      </TypeStyleSelection>
    )
  }
)``

const HeaderParagraph = styled(TypeStyle.BodyDefault)<{
  $paragraphTextSize: ParagraphTextSize
}>`
  ${o => TypeStyle.css[o.$paragraphTextSize ?? 'bodyDefault']}
`

const textAlignToJustifyContent = (textAlign: TextAlign) =>
  textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'

const HeaderLinks = styled.div<{
  $textAlignMobile: TextAlign
  $textAlignDesktop: TextAlign
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${o => o.theme.spacing(5)};
  padding-block: ${o => o.theme.spacing(1)};

  justify-content: ${o => textAlignToJustifyContent(o.$textAlignMobile)};

  ${fromMd} {
    justify-content: ${o => textAlignToJustifyContent(o.$textAlignDesktop)};
  }
`

const StyledContentfulStyledLink = styled(ContentfulStyledLink)`
  display: inline-flex;
  align-items: center;
  height: min-content;
`

// Override lmf's TabGroupList to remove the border and the animated indicator.
const SectionHeaderTabList = styled(TabGroupList)`
  border-bottom: 0;
  overflow: visible;

  [role='tablist'] {
    gap: ${o => o.theme.spacing(5)};
  }

  > div:last-child {
    display: none;
  }
`

const SectionHeaderTab = styled(TabGroupTab)<{ $isUppercase: boolean }>`
  ${TypeStyle.css.headlineExtraExtraSmall}
  padding: 0;
  color: ${o => o.theme.color.text.tertiary.on.background};
  text-transform: none;

  border-style: solid;
  border-width: 0;
  border-bottom-width: ${o => o.theme.spacing(0.5)};
  border-color: transparent;
  &[aria-selected='true'] {
    color: inherit;
    border-color: currentColor;
  }
  height: min-content;
  ${o => o.$isUppercase && o.theme.typography.effect.uppercase}
`

type SectionHeaderTabsProps = {
  'tabItems': SectionHeaderTabItem[]
  'aria-label'?: string
}

/** Must render inside a lmf TabGroupProvider. */
const SectionHeaderTabs = ({ tabItems, 'aria-label': ariaLabel }: SectionHeaderTabsProps) => {
  return (
    <SectionHeaderTabList aria-label={ariaLabel}>
      {tabItems.map(tab => (
        <SectionHeaderTab key={tab.id} id={tab.id} $isUppercase={isUppercase(tab.text)}>
          {tab.text}
        </SectionHeaderTab>
      ))}
    </SectionHeaderTabList>
  )
}

type HeaderRichTextBase = {
  document: NonNullable<ReturnType<typeof getNonEmptySanitizedContentfulDocument>>
}

type HeaderRichTextProps = HeaderRichTextBase &
  (
    | { field: 'title'; textSize: TitleTextSize; asTag?: HeadingTag }
    | { field: 'paragraph'; textSize: ParagraphTextSize }
  )

const HeaderRichText = (props: HeaderRichTextProps) => {
  const { document, field, textSize } = props
  const titleAsTag = props.field === 'title' ? props.asTag : undefined
  const plainText = useMemo(() => getContentfulRichTextPlainText(document), [document])
  const renderMark = useMemo(() => createDefaultRichTextRenderMark(), [])
  const renderHyperlink = useMemo(() => createRichTextHyperlinkRenderer(), [])

  const renderOptions: Options = useMemo(
    () => ({
      renderMark,
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_node, children) =>
          field === 'title' ? (
            <HeaderTitle
              titleTextSize={textSize}
              asTag={titleAsTag}
              isUppercase={isUppercase(plainText)}
            >
              {children}
            </HeaderTitle>
          ) : (
            <HeaderParagraph color='primary' $paragraphTextSize={textSize} asTag='p'>
              {children}
            </HeaderParagraph>
          ),
        [INLINES.HYPERLINK]: renderHyperlink,
      },
    }),
    [renderMark, renderHyperlink, field, textSize, titleAsTag, plainText]
  )

  return <>{documentToReactComponents(document, renderOptions)}</>
}

export const ContentfulPageSectionHeader = ({
  titleRichText,
  titleTextSize,
  paragraphRichText,
  paragraphTextSize,
  textContentMaxWidthMobile,
  textContentMaxWidthDesktop,
  textAlignMobile,
  textAlignDesktop,
  links = [],
  tabItems,
  titleAsTag,
  spacingBelow = 'default',
}: ContentfulPageSectionHeaderProps) => {
  const title = getNonEmptySanitizedContentfulDocument(titleRichText)
  const paragraph = getNonEmptySanitizedContentfulDocument(paragraphRichText)

  const hasTabs = (tabItems?.length ?? 0) > 0
  const tabListLabel = useMemo(() => (title ? getContentfulRichTextPlainText(title) : ''), [title])

  return (
    <HeaderOverlay
      $textAlignMobile={textAlignMobile}
      $textAlignDesktop={textAlignDesktop}
      $spacingBelow={spacingBelow}
    >
      {title || paragraph ? (
        <HeaderText
          $textAlignMobile={textAlignMobile}
          $textAlignDesktop={textAlignDesktop}
          $textContentMaxWidthMobile={textContentMaxWidthMobile}
          $textContentMaxWidthDesktop={textContentMaxWidthDesktop}
        >
          {title ? (
            <HeaderRichText
              document={title}
              field='title'
              textSize={titleTextSize}
              asTag={titleAsTag}
            />
          ) : null}

          {paragraph ? (
            <HeaderRichText document={paragraph} field='paragraph' textSize={paragraphTextSize} />
          ) : null}
        </HeaderText>
      ) : null}

      {(links.length > 0 || hasTabs) && (
        <HeaderLinks $textAlignMobile={textAlignMobile} $textAlignDesktop={textAlignDesktop}>
          {links.map((link, index) => (
            <StyledContentfulStyledLink
              {...link}
              key={index}
              $isUppercase={isUppercase(link.text)}
            />
          ))}
          {tabItems && hasTabs && (
            <SectionHeaderTabs tabItems={tabItems} aria-label={tabListLabel || undefined} />
          )}
        </HeaderLinks>
      )}
    </HeaderOverlay>
  )
}
